import { useState } from 'react';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import { Button, Card, CheckboxControl } from '@/design-system';
import type { MigrationLegacyClient } from '../types';
import { isPhoneLinkedToV2 } from '../utils/migration-format';
import { formatPhone } from '@/design-system/utils/masks';

export interface LegacyClientDetailsPanelProps {
  client: MigrationLegacyClient | null;
  loading?: boolean;
  selectedPhoneIds: string[];
  onTogglePhone: (phoneId: string) => void;
  onSelectAllPhones: () => void;
  onClearActiveClientPhones: () => void;
}

export function LegacyClientDetailsPanel({
  client,
  loading = false,
  selectedPhoneIds,
  onTogglePhone,
  onSelectAllPhones,
  onClearActiveClientPhones,
}: LegacyClientDetailsPanelProps) {
  const [expandedPhones, setExpandedPhones] = useState(true);
  const linkedCount = client?.phones.filter(isPhoneLinkedToV2).length ?? 0;

  return (
    <Card>
      <div className="border-b border-gray-100 px-5 py-4">
        <h3 className="text-lg font-semibold text-gray-900">Detalhes do cliente</h3>
      </div>

      {loading ? (
        <div className="space-y-3 p-5">
          <div className="h-20 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
        </div>
      ) : !client ? (
        <div className="p-6 text-sm text-gray-500">
          Selecione um cliente na lista para ver os telefones.
        </div>
      ) : (
        <div className="space-y-4 p-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-base font-semibold text-gray-900">{client.name}</h4>
                <p className="text-sm text-gray-500">{client.ownerEmail || ''}</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm">
                <Users size={14} />
                {client.phones.length} telefones · {linkedCount} no v2
              </div>
            </div>

            <div className="mt-4">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">ID legado</div>
                <div className="mt-1 break-all text-sm font-medium text-gray-900">{client.legacyId}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h5 className="text-sm font-semibold text-gray-900">Telefones associados</h5>
                <p className="text-xs text-gray-500">
                  Apenas números conectados no v2 podem ser selecionados para migração.
                </p>
              </div>
              <Button variant="outline" size="sm"
                type="button"
                onClick={() => setExpandedPhones((prev) => !prev)}
                aria-expanded={expandedPhones}
                className="text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                {expandedPhones ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {expandedPhones ? 'Ocultar lista' : 'Exibir lista'}
              </Button>
            </div>

            {client.phones.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                Nenhum telefone WABA encontrado para este parceiro.
              </div>
            ) : expandedPhones ? (
              <div className="space-y-2">
                {client.phones.map((phone) => {
                  const linked = isPhoneLinkedToV2(phone);
                  const checked = selectedPhoneIds.includes(phone.id);
                  return (
                    <div
                      key={phone.id}
                      className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                        !linked
                          ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-70'
                          : checked
                            ? 'cursor-pointer border-[#0084FF] bg-[#0084FF]/5'
                            : 'cursor-pointer border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <CheckboxControl
                        id={`legacy-phone-${client.legacyId}-${phone.id}`}
                        checked={checked}
                        disabled={!linked}
                        onCheckedChange={() => {
                          if (linked) onTogglePhone(phone.id);
                        }}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#0084FF] focus:ring-[#0084FF] disabled:cursor-not-allowed"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <label htmlFor={`legacy-phone-${client.legacyId}-${phone.id}`} className="cursor-pointer text-sm font-medium text-gray-900">
                            {phone.label}
                          </label>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              linked
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-800'
                            }`}
                          >
                            {linked ? 'Conectado no v2' : 'Não cadastrado no v2'}
                          </span>
                        </div>
                        <p className="mt-1 font-mono text-sm text-gray-700">{formatPhone(phone.number)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                {client.phones.length} telefone(s) pronto(s) para seleção.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onSelectAllPhones} disabled={linkedCount === 0}>
              Selecionar todos os números
            </Button>
            <Button variant="ghost" onClick={onClearActiveClientPhones}>
              Limpar seleção deste cliente
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
